// /team/playbooks: reusable jobs that start a chat with frozen instructions.
// Mounted inside TeamPanel so it shares the shell, theme, and /teams/me.
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Theme, themeColors } from '../theme/colors';
import {
  TeamMe,
  PlaybookRow,
  PlaybookListPayload,
  PlaybookAnalytics,
  PlaybookInputField,
  TeamError,
  TeamUnauthorizedError,
  listPlaybooks,
  getPlaybook,
  createPlaybook,
  updatePlaybook,
  publishPlaybook,
  startPlaybook,
  getPlaybookAnalytics,
} from './chat/teamService';
import TeamGuide from './TeamGuide';

export interface TeamPlaybooksProps {
  theme: Theme;
  me: TeamMe;
}

const emptyDraft = {
  name: '',
  description: '',
  instructions: '',
  output_format: '',
  example_output: '',
  without: '',
  withAi: '',
};

const SAMPLE_DRAFT = {
  name: 'Laporan kunjungan',
  description: 'Ringkasan kunjungan klien, siap dikirim ke atasan.',
  instructions:
    'Kamu membantu sales merangkum kunjungan klien.\n\nTanyakan yang belum ada: nama klien, tanggal kunjungan, apa yang dibahas, keputusan, dan kendala.\n\nTulis ringkas dalam bahasa Indonesia percakapan. Jangan mengarang angka, janji, atau nama yang tidak disebut pengguna.',
  output_format: 'Lima poin bernomor: klien, tanggal, hasil, kendala, langkah berikutnya.',
  example_output:
    '1. Klien: PT Maju Jaya\n2. Tanggal: 12 Agustus 2026\n3. Hasil: setuju trial 14 hari untuk 3 cabang\n4. Kendala: butuh persetujuan finance\n5. Langkah berikutnya: kirim penawaran tertulis Jumat ini',
  without: '30',
  withAi: '5',
};

function timeLine(row: PlaybookRow): string {
  const version = row.published_version || row.version;
  const withAi = version?.estimated_minutes_with_ai;
  const without = version?.estimated_minutes_without_ai;
  if (withAi && without) {
    return `Tanpa AI sekitar ${without} menit. Dengan Power AI sekitar ${withAi} menit.`;
  }
  if (withAi) return `Dengan Power AI sekitar ${withAi} menit.`;
  if (without) return `Tanpa AI sekitar ${without} menit.`;
  return '';
}

function requiredInputs(row: PlaybookRow): PlaybookInputField[] {
  const schema = row.published_version?.input_schema || row.version?.input_schema || [];
  return schema.filter((field) => field.required);
}

const TeamPlaybooks: React.FC<TeamPlaybooksProps> = ({ theme }) => {
  const c = themeColors[theme];
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/team\/playbooks\/([^/]+)/);
  const detailUuid = match ? match[1] : null;

  const [payload, setPayload] = useState<PlaybookListPayload | null>(null);
  const [detail, setDetail] = useState<PlaybookRow | null>(null);
  const [analytics, setAnalytics] = useState<PlaybookAnalytics | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editInstructions, setEditInstructions] = useState('');
  const [inputs, setInputs] = useState<Record<string, string>>({});

  const cardStyle: React.CSSProperties = {
    background: c.panel,
    border: `1px solid ${c.line}`,
    borderRadius: '12px',
    padding: '18px',
  };
  const sectionLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: c.ink3,
    margin: '26px 2px 10px',
  };
  const inputStyle: React.CSSProperties = {
    background: c.panel2,
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '13.5px',
    color: c.ink,
    outline: 'none',
    fontFamily: 'inherit',
    minWidth: 0,
    width: '100%',
  };
  const ghostBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${c.line2}`,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: c.ink,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
  };
  const primaryBtnStyle: React.CSSProperties = {
    background: c.btnPrimaryBg,
    color: c.btnPrimaryText,
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: '44px',
    ['--pa-btn-hover' as any]: c.btnPrimaryHover,
  };

  const describe = (err: unknown) => {
    if (err instanceof TeamUnauthorizedError) {
      window.location.href = '/login';
      return '';
    }
    if (err instanceof TeamError) return err.detail;
    return 'Gagal memuat. Coba lagi.';
  };

  const load = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      const listed = await listPlaybooks();
      setPayload(listed);
      if (detailUuid) {
        const row = await getPlaybook(detailUuid);
        setDetail(row);
        setEditInstructions(row.version?.instructions || '');
        setInputs({});
        try {
          setAnalytics(await getPlaybookAnalytics(detailUuid));
        } catch {
          setAnalytics(null);
        }
      } else {
        setDetail(null);
        setAnalytics(null);
      }
      setStatus('ready');
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
      setStatus('error');
    }
  }, [detailUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  const canDraft = !!payload?.can_draft;
  const canPublish = !!payload?.can_publish;
  const rows = payload?.playbooks ?? [];

  const handleCreate = async () => {
    if (!draft.name.trim() || busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const without = draft.without.trim() ? Number(draft.without) : undefined;
      const withAi = draft.withAi.trim() ? Number(draft.withAi) : undefined;
      const created = await createPlaybook({
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        instructions: draft.instructions.trim() || undefined,
        output_format: draft.output_format.trim() || undefined,
        example_output: draft.example_output.trim() || undefined,
        estimated_minutes_without_ai: Number.isFinite(without as number) ? without : undefined,
        estimated_minutes_with_ai: Number.isFinite(withAi as number) ? withAi : undefined,
      });
      setDraft(emptyDraft);
      setNotice('Draft Playbook tersimpan.');
      navigate(`/team/playbooks/${created.uuid}`);
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveInstructions = async () => {
    if (!detail || busy) return;
    setBusy(true);
    setError('');
    try {
      const row = await updatePlaybook(detail.uuid, { instructions: editInstructions });
      setDetail(row);
      setNotice('Draft tersimpan. Publish supaya anggota memakai versi ini.');
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handlePublish = async (uuid: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await publishPlaybook(uuid);
      setNotice('Playbook sudah dipublish.');
      await load();
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleStart = async (row: PlaybookRow, values?: Record<string, string>) => {
    if (busy) return;
    const needed = requiredInputs(row);
    if (needed.length > 0 && !detailUuid) {
      navigate(`/team/playbooks/${row.uuid}`);
      return;
    }
    for (const field of needed) {
      if (!(values || inputs)[field.key]?.trim()) {
        setError(`Kolom ${field.label} wajib diisi.`);
        return;
      }
    }
    setBusy(true);
    setError('');
    try {
      const started = await startPlaybook(row.uuid, values || inputs);
      navigate(`/c/${started.conversation_uuid}`);
    } catch (err) {
      const msg = describe(err);
      if (msg) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const renderCard = (row: PlaybookRow) => {
    const estimate = timeLine(row);
    const published = row.status === 'published';
    return (
      <div key={row.uuid} style={{ ...cardStyle, marginTop: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>{row.name}</div>
            {row.description && (
              <div style={{ fontSize: '12.5px', color: c.ink2, marginTop: '4px', lineHeight: 1.6 }}>
                {row.description}
              </div>
            )}
            <div style={{ fontSize: '12px', color: c.ink3, marginTop: '8px' }}>
              {published ? 'Published' : 'Draft'}
              {estimate ? ` · ${estimate}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {published && (
              <button
                type="button"
                onClick={() => handleStart(row)}
                disabled={busy}
                className="pa-primary"
                style={{ ...primaryBtnStyle, opacity: busy ? 0.45 : 1 }}
              >
                Mulai di Power AI
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(`/team/playbooks/${row.uuid}`)}
              className="pa-ghost"
              style={ghostBtnStyle}
            >
              Lihat
            </button>
            {canPublish && row.status === 'draft' && (
              <button
                type="button"
                onClick={() => handlePublish(row.uuid)}
                disabled={busy}
                className="pa-ghost"
                style={ghostBtnStyle}
              >
                Publish
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (status === 'loading' && !payload) {
    return <div style={{ fontSize: '13px', color: c.ink2 }}>Memuat Playbook…</div>;
  }

  if (detailUuid && detail) {
    const schema = (detail.published_version || detail.version)?.input_schema || [];
    const estimate = timeLine(detail);
    const published = detail.status === 'published';
    return (
      <>
        <button
          type="button"
          onClick={() => navigate('/team/playbooks')}
          className="pa-ghost"
          style={{ ...ghostBtnStyle, marginBottom: '8px' }}
        >
          Semua Playbook
        </button>
        <div style={sectionLabelStyle}>Playbook</div>
        {error && (
          <div style={{ fontSize: '12.5px', color: theme === 'light' ? '#DC2626' : '#F87171', marginBottom: '10px' }}>
            {error}
          </div>
        )}
        {notice && (
          <div style={{ fontSize: '12.5px', color: c.ink2, marginBottom: '10px' }}>{notice}</div>
        )}
        <div style={cardStyle}>
          <div style={{ fontSize: '18px', fontWeight: 650, color: c.ink }}>{detail.name}</div>
          {detail.description && (
            <div style={{ fontSize: '13.5px', color: c.ink2, marginTop: '8px', lineHeight: 1.65 }}>
              {detail.description}
            </div>
          )}
          {estimate && (
            <div style={{ fontSize: '12.5px', color: c.ink3, marginTop: '10px' }}>{estimate}</div>
          )}
          <div style={{ fontSize: '13px', color: c.ink2, marginTop: '14px', lineHeight: 1.7 }}>
            {published
              ? 'Tekan Mulai di Power AI. Chat baru terbuka dengan sapaan. Ceritakan faktanya, misalnya "Tadi ketemu PT Maju Jaya, setuju trial." Setelah hasil Playbook keluar, tekan Tandai selesai di atas thread. Boleh lanjut tanya.'
              : canPublish
                ? 'Ini masih draft. Anggota belum bisa memulai. Cek instruksinya, lalu tekan Publish. Setelah itu tombol Mulai di Power AI muncul.'
                : 'Ini masih draft. Admin yang menekan Publish. Setelah itu, tekan Mulai di Power AI untuk membuka chat baru.'}
          </div>
          {(detail.published_version || detail.version)?.example_output && (
            <div style={{ fontSize: '13px', color: c.ink2, marginTop: '14px', lineHeight: 1.65 }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: c.ink3, marginBottom: '4px' }}>
                CONTOH HASIL
              </div>
              {(detail.published_version || detail.version)?.example_output}
            </div>
          )}
          {schema.length > 0 && published && (
            <div style={{ marginTop: '16px', display: 'grid', gap: '10px' }}>
              {schema.map((field) => (
                <label key={field.key} style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontSize: '12px', color: c.ink2 }}>
                    {field.label}{field.required ? ' (wajib)' : ''}
                  </span>
                  <input
                    className="pa-field"
                    value={inputs[field.key] || ''}
                    onChange={(e) => setInputs((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
            {published && (
              <button
                type="button"
                onClick={() => handleStart(detail, inputs)}
                disabled={busy}
                className="pa-primary"
                style={{ ...primaryBtnStyle, opacity: busy ? 0.45 : 1 }}
              >
                Mulai di Power AI
              </button>
            )}
            {canPublish && (
              <button
                type="button"
                onClick={() => handlePublish(detail.uuid)}
                disabled={busy}
                className="pa-ghost"
                style={ghostBtnStyle}
              >
                Publish
              </button>
            )}
          </div>
        </div>
        {detail.can_edit && (
          <>
            <div style={sectionLabelStyle}>Instruksi (hanya editor)</div>
            <div style={cardStyle}>
              <textarea
                value={editInstructions}
                onChange={(e) => setEditInstructions(e.target.value)}
                rows={8}
                className="pa-field"
                style={{ ...inputStyle, resize: 'vertical', minHeight: '140px' }}
              />
              <button
                type="button"
                onClick={handleSaveInstructions}
                disabled={busy}
                className="pa-ghost"
                style={{ ...ghostBtnStyle, marginTop: '10px' }}
              >
                Simpan draft
              </button>
            </div>
          </>
        )}
        {analytics && (
          <>
            <div style={sectionLabelStyle}>Hasil</div>
            <div style={cardStyle}>
              <div style={{ fontSize: '13px', color: c.ink2, lineHeight: 1.7 }}>
                {analytics.starts} mulai · {analytics.completions} selesai · {analytics.useful_yes} berguna · {analytics.credits} kredit
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div style={sectionLabelStyle}>Playbook</div>
      <div style={cardStyle}>
        <TeamGuide
          theme={theme}
          lead={[
            'Playbook adalah SOP yang sudah ditanam ke chat. Anggota buka pekerjaan ini, Power AI sudah tahu harus ngerjain apa.',
            'Sari tidak merakit prompt. Dia bawa fakta kunjungan hari ini. Power AI yang menulis laporan.',
          ]}
          exampleName="Laporan kunjungan"
          exampleBody={[
            'Sari di sales harus kirim laporan setiap habis meeting. Kalau dia buka chat kosong, dia pikir-pikir mau nulis apa, dan hasilnya beda-beda tiap hari.',
            'Kamu menulis instruksinya sekali: tanya nama klien, tanggal, hasil, kendala, lalu keluarkan lima poin. Sari tekan Mulai di Power AI. Chat baru terbuka dengan sapaan. Power AI sudah dapat instruksi itu di belakang layar.',
            'Sari ketik: "Tadi ketemu PT Maju Jaya, setuju trial 14 hari." Power AI menulis lima poin. Sari kirim ke atasan, lalu tekan Tandai selesai.',
          ]}
          columns={
            canDraft
              ? [
                  {
                    title: 'Kamu',
                    steps: [
                      'Tekan Pakai contoh di form bawah, atau tulis pekerjaan sendiri. Simpan draft.',
                      'Buka draft itu. Tekan Publish. Draft belum bisa dimulai anggota.',
                      'Tekan Mulai di Power AI. Ketik "Tadi ketemu PT Maju Jaya, setuju trial." Kalau jawabannya lima poin rapi, Playbook-nya sudah kerja.',
                    ],
                  },
                  {
                    title: 'Anggota, misalnya Sari',
                    steps: [
                      'Pilih Playbook di daftar ini, atau kartu Coba ini di chat baru.',
                      'Tekan Mulai. Chat menyapa, minta fakta kunjungan hari ini.',
                      'Isi faktanya. Terima hasilnya. Pakai (salin, kirim, rapat). Tekan Tandai selesai.',
                    ],
                  },
                ]
              : [
                  {
                    title: 'Cara pakai',
                    steps: [
                      'Pilih Playbook di daftar ini, atau kartu Coba ini di chat baru.',
                      'Tekan Mulai. Chat menyapa, minta fakta pekerjaan hari ini.',
                      'Isi faktanya. Terima hasilnya. Pakai, lalu tekan Tandai selesai.',
                    ],
                  },
                ]
          }
          faqs={[
            {
              q: 'Kenapa Sari masih ngetik di chat?',
              a: [
                'Setiap kunjungan beda klien, tanggal, dan keputusan. Itu fakta hari ini, hanya Sari yang punya. Chat adalah tempat dia menyerahkan fakta itu.',
                'SOP cara merangkumnya sudah tertulis di Playbook. Sari tidak perlu tahu cara prompting. Kalau atasan minta opsi restrukturisasi, dia tanya di thread yang sama.',
              ],
            },
            {
              q: 'Setelah fakta terisi, next step-nya apa?',
              a: [
                'Power AI menulis hasilnya, misalnya lima poin laporan. Itu deliverable-nya. Sari salin, kirim ke atasan, atau bawa ke rapat.',
                'Kalau hasilnya sudah dipakai, tekan Tandai selesai di atas thread. Chat tidak ditutup. Boleh lanjut tanya, atau buka Playbook yang sama lagi untuk kunjungan berikutnya.',
              ],
            },
            {
              q: 'Kenapa ini yang dihitung sebagai adopsi AI?',
              a: [
                'Orang gagal di chat kosong: tidak tahu mau nulis apa, coba sekali, balik ke WhatsApp. Playbook adalah pekerjaan yang Sari sudah kerjakan tiap minggu, tinggal dijalankan.',
                'Tandai selesai artinya hasilnya dipakai kerja sungguhan. Halaman Adopsi menghitung itu, bukan siapa yang cuma buka aplikasi. Program adopsi lalu menyuruh anggota baru: minggu ini kerjakan satu Playbook.',
              ],
            },
          ]}
          related={[
            { name: 'Project', body: 'Folder chat dan file untuk satu tujuan, misalnya Q3 Retail.' },
            { name: 'Grup', body: 'Siapa Sari: Sales atau CS. Playbook tetap jalan tanpa grup.' },
            { name: 'Program adopsi', body: 'PR onboarding. "Sari, minggu ini kerjakan satu Playbook." SOP-nya tetap di Playbook.' },
            { name: 'Adopsi', body: 'Papan skor pemilik. Siapa yang sudah rutin. Isi chat tidak tampil.' },
          ]}
        />
      </div>
      {error && (
        <div style={{ fontSize: '12.5px', color: theme === 'light' ? '#DC2626' : '#F87171', margin: '10px 2px 0' }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ fontSize: '12.5px', color: c.ink2, margin: '10px 2px 0' }}>{notice}</div>
      )}
      {canDraft && (
        <div style={{ ...cardStyle, marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: c.ink }}>Buat Playbook</div>
            <button
              type="button"
              onClick={() => setDraft(SAMPLE_DRAFT)}
              className="pa-ghost"
              style={ghostBtnStyle}
            >
              Pakai contoh
            </button>
          </div>
          <div style={{ fontSize: '12.5px', color: c.ink2, lineHeight: 1.65, marginBottom: '12px' }}>
            Dua langkah. Simpan draft dulu, lalu buka draft itu dan tekan Publish. Tombol Mulai di Power AI baru muncul setelah publish.
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Nama pekerjaan</span>
              <input
                className="pa-field"
                placeholder="Laporan kunjungan"
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                maxLength={80}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Hasil yang akan dibuat</span>
              <input
                className="pa-field"
                placeholder="Ringkasan kunjungan klien, siap dikirim ke atasan"
                value={draft.description}
                onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                maxLength={280}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Instruksi untuk Power AI</span>
              <textarea
                className="pa-field"
                placeholder="Apa yang harus ditanyakan, gaya bahasa, dan apa yang tidak boleh dikarang"
                value={draft.instructions}
                onChange={(e) => setDraft((prev) => ({ ...prev, instructions: e.target.value }))}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Format hasil</span>
              <input
                className="pa-field"
                placeholder="Lima poin bernomor: klien, tanggal, hasil, kendala, langkah berikutnya"
                value={draft.output_format}
                onChange={(e) => setDraft((prev) => ({ ...prev, output_format: e.target.value }))}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: c.ink2 }}>Contoh hasil yang sudah disetujui</span>
              <textarea
                className="pa-field"
                placeholder="Tulis satu hasil yang bagus, supaya anggota tahu bentuk yang diharapkan"
                value={draft.example_output}
                onChange={(e) => setDraft((prev) => ({ ...prev, example_output: e.target.value }))}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <label style={{ display: 'grid', gap: '6px', flex: '1 1 140px' }}>
                <span style={{ fontSize: '12px', color: c.ink2 }}>Menit tanpa AI</span>
                <input
                  className="pa-field"
                  placeholder="30"
                  value={draft.without}
                  onChange={(e) => setDraft((prev) => ({ ...prev, without: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: '6px', flex: '1 1 140px' }}>
                <span style={{ fontSize: '12px', color: c.ink2 }}>Menit dengan Power AI</span>
                <input
                  className="pa-field"
                  placeholder="5"
                  value={draft.withAi}
                  onChange={(e) => setDraft((prev) => ({ ...prev, withAi: e.target.value }))}
                  style={inputStyle}
                />
              </label>
            </div>
            <button
              type="button"
              onClick={handleCreate}
              disabled={busy || !draft.name.trim()}
              className="pa-primary"
              style={{ ...primaryBtnStyle, opacity: busy || !draft.name.trim() ? 0.45 : 1, justifySelf: 'start' }}
            >
              Simpan draft
            </button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <div style={{ ...cardStyle, marginTop: '12px', fontSize: '13px', color: c.ink2, lineHeight: 1.65 }}>
          {canDraft
            ? 'Belum ada Playbook. Isi form di atas, atau tekan Pakai contoh, lalu simpan dan publish.'
            : 'Belum ada Playbook. Admin atau Champion AI yang membuat yang pertama.'}
        </div>
      ) : (
        rows.map(renderCard)
      )}
    </>
  );
};

export default TeamPlaybooks;
